import {
  createTheme,
  Theme,
  TypographyVariantsOptions as TypographyOptions,
} from "@mui/material/styles";

const defaultTheme: Theme = createTheme();

/**
 * Playful pairs Outfit for headings, numbers and labels with Lato for body and
 * UI text. Both are loaded in `index.html`.
 *
 * `Login Reskin.dc.html` — the first mockup drawn natively in Playful — is
 * authoritative for this scale. It confirmed `h1` at 64px was wrong: that was
 * Playful's hero spec, while this app renders `h1` for ordinary page titles
 * ("Welcome back, Rebel!" on the mockup measures 32px). Every variant below
 * was corrected against that mockup where it appears there; where a variant
 * has no example in it, its prior value is kept rather than guessed at (each
 * variant's comment says which applies).
 *
 * Halving `h1` (64→32) left `h2` (30) almost exactly on top of it, so the
 * rest of the heading ramp (h2–h5) is rebalanced to keep each step visually
 * distinct: the mockup gives no evidence for h2–h5 sizes themselves, only for
 * the fact that h1 and h6 both moved, so the values below are chosen to keep
 * roughly even proportional gaps between adjacent steps (~12–25%) rather than
 * scaled by h1's own ratio — h1's *old* size was itself the bug (a hero value
 * doing a page-title's job), so its old proportion to the rest of the ramp
 * isn't a valid scaling reference.
 *
 * Where Playful is silent, the mapping is recorded on the variant itself:
 * Playful defines h1/h2/h3, body, UI labels and captions, whereas this theme
 * carries h1–h6, subtitle1/2, body1/2 and caption.
 */
/**
 * Outfit is display type ONLY, and always at weight 800. Measured in the design
 * guide: 51 Outfit spans against 238 Lato-inherited text nodes, at exactly
 * 64px (hero), 30px (section heading), 15/15.5px (button label), 14px and
 * 12.5px (strong labels), and 12px uppercase with 2.5px tracking (eyebrow).
 *
 * It never appears at 19, 20 or 17px — which is why h3, h4 and h5 are Lato 700
 * below, not Outfit. The design guide's builder card proves it: its "Problem"
 * title is 19px/700 with no `font-family` at all, so it inherits Lato. Putting
 * the display face on every heading variant was wrong, and made card titles
 * across the app render in the wrong face.
 */
export const DISPLAY_FONT = "'Outfit', 'Lato', sans-serif";

/**
 * No variant states `textAlign`. Eight of them used to carry
 * `textAlign: "left"` — defensively, against a global `text-align: center` that
 * no longer exists anywhere in `src/theme` or `src/css`. What it does now is
 * break every centred container: a variant's own class outranks the inherited
 * `text-align`, so a `Typography` inside a `textAlign: "center"` panel silently
 * went back to left while its unstyled siblings stayed centred (the AI critique
 * empty state read exactly that way — left title over centred body). Alignment
 * belongs to the box, so the box keeps it.
 */
const typography = {
  fontFamily: "'Lato', 'Roboto', sans-serif",
  /**
   * these typography values can be applied by 'light', 'regular', 'medium' and 'bold'
   * Usage:
   * =====
   *
     <Box sx={{ fontWeight: 'light' }}>
      theme.typography.fontWeightLight
     </Box>

     <Box sx={{ fontWeight: 'regular' }}>
      theme.typography.fontWeightRegular
     </Box>

     <Box sx={{ fontWeight: 'medium' }}>
      theme.typography.fontWeightMedium
     </Box>

     <Box sx={{ fontWeight: 500 }}>
       custom values
     </Box>

     <Box sx={{ fontWeight: 'bold' }}>
        theme.typography.fontWeightMedium
     </Box>
   *
   */
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 600,
  fontWeightBold: 700,
  /**
   * Page title. `Login Reskin.dc.html`'s "Welcome back, Rebel!" measures
   * Outfit 800, 32px, -1px tracking, 1.1 line-height — replacing the 64px
   * hero value that was never right for this role.
   *
   * DO NOT "correct" this back to 4rem/-2px/0.95. Playful's own theme sheet
   * states those numbers, and they are right — for a hero. This app renders
   * `h1` for ordinary page titles, and the mockup above is the measurement
   * that settles it. Deliberate divergence from the sheet.
   */
  h1: {
    fontFamily: DISPLAY_FONT,
    fontWeight: 800,
    fontSize: defaultTheme.typography.pxToRem(32),
    letterSpacing: "-1px",
    lineHeight: 1.1,
  },
  /**
   * Section heading. The design guide uses Outfit 800 at 30px with -0.5px
   * tracking for this role, nine times over — so it stays 30 rather than the
   * 24 it was briefly rebalanced to on no evidence. It sits close to `h1`
   * (32) by design: `h1` is a centred page title, `h2` an inline section
   * heading, and they rarely meet.
   */
  h2: {
    fontFamily: DISPLAY_FONT,
    fontWeight: 800,
    fontSize: defaultTheme.typography.pxToRem(30),
    letterSpacing: "-0.5px",
    lineHeight: 1.2,
  },
  /**
   * Card title. The design guide's builder card measures 19px/700 with no
   * `font-family`, so it inherits Lato — this is the variant that proved
   * Outfit does not belong on every heading.
   *
   * Playful's theme sheet says Outfit 22/800 here. Deliberate divergence: the
   * mockup that renders this role contradicts it, and Outfit at 19/22px
   * appears nowhere in the measured design guide.
   */
  h3: {
    fontWeight: 700,
    fontSize: defaultTheme.typography.pxToRem(19),
    letterSpacing: 0,
    lineHeight: 1.3,
  },
  /**
   * Fourth heading step, Lato 700 like `h3`. Was italic 16/300 — Playful has
   * no italic anywhere — so it keeps the size and drops the italic. Not in the
   * guide; sized one step under `h3`.
   */
  h4: {
    fontWeight: 700,
    fontSize: defaultTheme.typography.pxToRem(16),
    lineHeight: 1.35,
    letterSpacing: 0,
  },
  /**
   * Fifth heading step, Lato 700. Not in the guide; sized one step under
   * `h4` and clear of `h6` below.
   */
  h5: {
    fontWeight: 700,
    fontSize: defaultTheme.typography.pxToRem(14),
    lineHeight: 1.4,
    letterSpacing: 0,
  },
  /**
   * `h6` is an ordinary small heading, not the mockup's uppercase eyebrow —
   * it has 41 call sites across the app (Footer, Header, `Tags/Complete`,
   * `BuilderTopicCard`, and so on) that use it as a plain small heading, and
   * putting the eyebrow's Outfit/800/uppercase/tracking on the variant
   * shouted every one of them (a builder card description rendered "WHAT
   * NEED WILL YOU MEET?"). The eyebrow role states its own treatment
   * explicitly at its point of use instead (see `AuthShell.tsx`'s "LOGIN"
   * divider label) rather than borrowing it from `h6`. This reverts to
   * Playful's 13/700 UI label — the same role `subtitle1` already carries —
   * which is what these call sites actually want. The bare-element layer's
   * own uppercase rule (`h6:not(.MuiTypography-root)` in `Override.tsx`)
   * only ever applied to bare `<h6>` tags, never to this variant, and is
   * untouched.
   */
  h6: {
    fontWeight: 700,
    fontSize: defaultTheme.typography.pxToRem(13),
    lineHeight: 20 / 13,
    letterSpacing: 0,
  },
  /**
   * Button label: mockup measures Outfit 800, 15.5px (was unset — button
   * fell back to MUI's default size — and 700 weight).
   */
  button: {
    fontFamily: DISPLAY_FONT,
    textTransform: "initial",
    fontWeight: 800,
    fontSize: defaultTheme.typography.pxToRem(15.5),
    letterSpacing: 0,
  },
  /**
   * Playful UI label: 13/700. The mockup confirms two roles at this exact
   * spec — the field label ("Email") and the inline link — so both are this
   * variant; no change from the prior value.
   *
   * Playful's theme sheet says Outfit 17/600. Deliberate divergence: this
   * variant *is* the field-label role, `Field.tsx` and ~16 `InputLabel` call
   * sites render through it, and the mockup measures every one of them at
   * 13/700 in Lato.
   */
  subtitle1: {
    fontSize: defaultTheme.typography.pxToRem(13),
    lineHeight: 20 / 13,
    letterSpacing: 0,
    fontWeight: 700,
  },
  /**
   * Error title: mockup measures Lato 14px/700 (was 12/400, Playful's UI
   * label). Nothing else in the theme's fixed variant list was free for this
   * role, and it is otherwise unused, so it is repurposed here rather than
   * left at a spec the mockup contradicts.
   */
  subtitle2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    lineHeight: 20 / 12,
    fontWeight: 700,
    letterSpacing: 0,
  },
  /**
   * Playful body: 15/400. The mockup's input text measures the same — no
   * change.
   */
  body1: {
    fontSize: defaultTheme.typography.pxToRem(15),
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: 0,
  },
  /**
   * Footer/helper text: mockup measures Lato 14.5px (was 13), rendered in
   * `text.secondary` at its call sites (colour is applied via the `color`
   * prop, as with every other variant here — never baked into the variant).
   */
  body2: {
    fontSize: defaultTheme.typography.pxToRem(14.5),
    fontWeight: 400,
    lineHeight: 20 / 13,
    letterSpacing: 0,
  },
  /**
   * Field-level error: mockup measures Lato 12px/700 (was 13/700). This is
   * also MUI's `FormHelperText` base style — it spreads `theme.typography.
   * caption` directly (see `FormHelperText.js`) — so every field's
   * validation message across the app already renders through this variant;
   * shrinking it 1px is what the mockup's field-level error asks for.
   */
  caption: {
    display: "inline-block",
    fontSize: defaultTheme.typography.pxToRem(12),
    lineHeight: 18 / 13,
    letterSpacing: 0,
    fontWeight: 700,
  },
  /**
   * Playful's eyebrow: 11px, 800, 1.4px tracking, uppercase. This is the role
   * `h6` was briefly given and had to give back — `h6` has 41 call sites using
   * it as a plain small heading, and shouting all of them was the bug. `overline`
   * is MUI's own variant for exactly this treatment and had no override, so the
   * eyebrow lives here instead and call sites that want it ask for it by name.
   *
   * `textTransform` is on the variant, unlike every other variant here: an
   * eyebrow that is not uppercase is not an eyebrow, so there is no call site
   * that wants this variant without it.
   */
  overline: {
    fontSize: defaultTheme.typography.pxToRem(11),
    fontWeight: 800,
    letterSpacing: "1.4px",
    textTransform: "uppercase",
  },
  /**
   * In-page section heading — "Upcoming", "Past events", "Your groups". Outfit
   * 800 at 24px with -0.4px tracking, measured identically in three sheets:
   * `Hub Events.dc.html` (2×), `Hub Groups.dc.html` (2×) and
   * `Hub Dashboard.dc.html` (3×).
   *
   * The ramp had nothing at this size. `h2` (30px) is the hub header band's
   * title, one step above; `h3` is Lato 19/700, a card title. So every section
   * heading in the hub pages was rendering through `h3` — the wrong face, two
   * steps small — because it was the closest thing available.
   */
  sectionHeading: {
    fontFamily: DISPLAY_FONT,
    fontWeight: 800,
    fontSize: defaultTheme.typography.pxToRem(24),
    letterSpacing: "-0.4px",
    lineHeight: 1.2,
  },
  /**
   * The title of a card that is itself the page's content — the upcoming event
   * card, measured at Outfit 800 21px / 1.2 in `Hub Events.dc.html`.
   *
   * Distinct from `h3`, which stays Lato 19/700 for the *nested* card title
   * the Builder sheet measures (see `h3`'s own note). A card whose whole job
   * is to present one record leads with the display face; a card sitting
   * inside a form does not.
   */
  cardTitle: {
    fontFamily: DISPLAY_FONT,
    fontWeight: 800,
    fontSize: defaultTheme.typography.pxToRem(21),
    letterSpacing: "-0.2px",
    lineHeight: 1.2,
  },
  /**
   * The title of one row inside a list card — a past event, measured at Outfit
   * 800 16.5px / 1.25 in `Hub Events.dc.html`. One step under `cardTitle`: a
   * row is a record too, so it keeps the display face, but it is scanned in a
   * stack rather than read on its own.
   */
  rowTitle: {
    fontFamily: DISPLAY_FONT,
    fontWeight: 800,
    fontSize: defaultTheme.typography.pxToRem(16.5),
    letterSpacing: 0,
    lineHeight: 1.25,
  },
} as TypographyOptions;

export default typography;
